/**
 * The Composer's live mix, as arithmetic (Dax, 2026-09-29). The preview is mixed in the browser the way
 * the picture is cut from proxies: every channel, level, mute and sound is placed on the cut's timeline
 * here, and the engine (preview-mix.ts) only plays what this says. Pure, so the placement is tested.
 */
import { CLIP_DUCK_DB, SFX_NUDGE_MAX_MS, SFX_NUDGE_MIN_OVERLAP_MS, channelHeard, placeSound, type AudioChannel, type AudioFx, type DraftMutes, type DraftSolos, type SlotAudio } from '@studio/contracts';

/** A playable file and the stretch of it that plays: `fromMs` into the file, for `durationMs`. */
export interface MixLayer { url: string; fromMs: number; durationMs: number }
/**
 * A layer placed on the cut's timeline at `atMs` (negative: it began before the cut). A clip's sound
 * effect carries the ramps its trim gives it (`placeSound`), at its own start and end.
 */
export interface MixSound { layer: MixLayer; atMs: number; gainDb: number; fadeInMs?: number; fadeOutMs?: number }

export interface MixPlan {
  totalMs: number;
  /** Each slot on the cut's timeline, with its own sound's level; null is silent. */
  slots: { startMs: number; endMs: number; gainDb: number | null }[];
  /** Each channel's level; null is muted. */
  buses: Record<AudioChannel, number | null>;
  /** The ASMR channel: each clip's sound effect, its trimmed stretch where it was pushed. */
  sounds: MixSound[];
  /** The full-length channels' segments, each its stretch in place at its own level. */
  music: MixSound[];
  soundtrack: MixSound[];
  voice: MixSound[];
  fx: AudioFx;
  /** The whole mix's level, after the EQ. */
  masterGainDb: number;
}

export interface MixDraft {
  slots: { inMs: number; outMs: number; gainDb: number; audio: SlotAudio; audio2: { gainDb: number; offsetMs: number; muted: boolean; trimFromMs: number; trimToMs: number | null; endFadeMs: number | null } | null }[];
  /** Each slot's sound effect file, whole, in slot order; null where it has none. */
  sounds: (MixLayer | null)[];
  /**
   * Each full-length channel's segments, with each one's whole file (null while its file has not loaded):
   * `atMs` is where the file starts on the cut, `fromMs` to `toMs` the stretch that plays.
   */
  layers: Record<'music' | 'voice' | 'soundtrack', { seg: { atMs: number; fromMs: number; toMs: number | null; gainDb: number }; file: MixLayer | null }[]>;
  clipsGainDb: number;
  asmrGainDb: number;
  bedGainDb: number;
  musicGainDb: number;
  voiceGainDb: number;
  masterGainDb: number;
  mutes: DraftMutes;
  solos: DraftSolos;
  audioFx: AudioFx;
}

export function planMix(d: MixDraft): MixPlan {
  const starts: number[] = [];
  let at = 0;
  for (const s of d.slots) {
    starts.push(at);
    at += s.outMs - s.inMs;
  }
  const totalMs = at;
  const slots = d.slots.map((s, i) => ({
    startMs: starts[i]!,
    endMs: starts[i]! + (s.outMs - s.inMs),
    gainDb: s.audio === 'mute' ? null : s.gainDb + (s.audio === 'duck' ? CLIP_DUCK_DB : 0),
  }));
  const sounds: MixSound[] = d.slots.flatMap((s, i) => {
    const layer = d.sounds[i];
    if (!s.audio2 || s.audio2.muted || !layer) return [];
    // The trimmed stretch, in place, cut off before the first frame and after the last, as Export does.
    const p = placeSound({ atMs: starts[i]! + s.audio2.offsetMs, fileMs: layer.durationMs, fromMs: s.audio2.trimFromMs, toMs: s.audio2.trimToMs, endFadeMs: s.audio2.endFadeMs }, totalMs);
    return p ? [{ layer: { url: layer.url, fromMs: layer.fromMs + p.fromMs, durationMs: p.durationMs }, atMs: p.atMs, gainDb: s.audio2.gainDb, fadeInMs: p.fadeInMs, fadeOutMs: p.fadeOutMs }] : [];
  });
  /** A channel's segments, each its trimmed stretch placed where it plays, at its own level. */
  const segments = (list: MixDraft['layers']['music']): MixSound[] => list.flatMap(({ seg, file }) => {
    if (!file) return [];
    const from = Math.max(0, Math.min(seg.fromMs, file.durationMs));
    const to = Math.max(from, Math.min(seg.toMs ?? file.durationMs, file.durationMs));
    return to > from ? [{ layer: { url: file.url, fromMs: file.fromMs + from, durationMs: to - from }, atMs: seg.atMs + from, gainDb: seg.gainDb }] : [];
  });
  return {
    totalMs,
    slots,
    // A channel is heard when it is not muted and, while anything is soloed, is soloed itself.
    buses: {
      clips: channelHeard('clips', d.mutes, d.solos) ? d.clipsGainDb : null,
      asmr: channelHeard('asmr', d.mutes, d.solos) ? d.asmrGainDb : null,
      music: channelHeard('music', d.mutes, d.solos) ? d.musicGainDb : null,
      voice: channelHeard('voice', d.mutes, d.solos) ? d.voiceGainDb : null,
      soundtrack: channelHeard('soundtrack', d.mutes, d.solos) ? d.bedGainDb : null,
    },
    sounds,
    music: segments(d.layers.music),
    soundtrack: segments(d.layers.soundtrack),
    voice: segments(d.layers.voice),
    fx: d.audioFx,
    masterGainDb: d.masterGainDb,
  };
}

/** One sound of the whole mix: `fromMs` into `url` for `durationMs`, starting `atMs` on the cut, at its level and its channel's. */
export interface MixStem { url: string; atMs: number; fromMs: number; durationMs: number; gainDb: number }

/**
 * Every sound the plan plays across the whole cut, for mixing it down. `clips` is each slot's own file and where
 * its stretch starts; a clip with none, a muted clip and a muted channel are left out.
 */
export function mixStems(plan: MixPlan, clips: ({ url: string; inMs: number } | null)[]): MixStem[] {
  const placed = (list: MixSound[], busDb: number | null): MixStem[] => list.flatMap((s) => {
    const w = busDb === null ? null : soundWindow(s, 0, plan.totalMs);
    return w && busDb !== null ? [{ url: s.layer.url, atMs: w.delayMs, fromMs: w.offsetMs, durationMs: w.lengthMs, gainDb: s.gainDb + busDb }] : [];
  });
  const clipsDb = plan.buses.clips;
  return [
    ...plan.slots.flatMap((s, i) => {
      const clip = clips[i];
      return clip && s.gainDb !== null && clipsDb !== null ? [{ url: clip.url, atMs: s.startMs, fromMs: clip.inMs, durationMs: s.endMs - s.startMs, gainDb: s.gainDb + clipsDb }] : [];
    }),
    ...placed(plan.sounds, plan.buses.asmr),
    ...placed(plan.music, plan.buses.music),
    ...placed(plan.voice, plan.buses.voice),
    ...placed(plan.soundtrack, plan.buses.soundtrack),
  ];
}

/**
 * What of a placed sound is heard when the cut plays from `fromMs` to `untilMs`: how long after the
 * start it begins, where in the file, and for how long. Null when none of it falls inside.
 */
export function soundWindow(s: MixSound, fromMs: number, untilMs: number): { delayMs: number; offsetMs: number; lengthMs: number } | null {
  const begins = Math.max(s.atMs, fromMs);
  const ends = Math.min(s.atMs + s.layer.durationMs, untilMs);
  if (ends <= begins) return null;
  return { delayMs: begins - fromMs, offsetMs: s.layer.fromMs + (begins - s.atMs), lengthMs: ends - begins };
}

/** The slot playing at a moment of the cut; the last one at its very end. */
export function slotAt(plan: MixPlan, ms: number): number {
  const i = plan.slots.findIndex((s) => ms >= s.startMs && ms < s.endMs);
  return i >= 0 ? i : plan.slots.length - 1;
}

export function dbToGain(db: number | null): number {
  return db === null ? 0 : Math.pow(10, db / 20);
}

/**
 * Where a sound may be pushed: never more than `limitMs` either way, and always with some of it inside its
 * own window, so it can be grabbed again. The card's lane and the ASMR strip clamp by this one rule.
 */
export function clampSoundOffset(ms: number, durationMs: number, windowMs: number, limitMs = SFX_NUDGE_MAX_MS): number {
  const lo = Math.max(-limitMs, -(durationMs - SFX_NUDGE_MIN_OVERLAP_MS));
  const hi = Math.min(limitMs, windowMs - SFX_NUDGE_MIN_OVERLAP_MS);
  return Math.round(Math.max(lo, Math.min(hi, ms)));
}

/** How far a slot's footage may slip either way: its edges stay inside the snippet's bounds. */
export function clampSlip(deltaMs: number, range: { inMs: number; outMs: number }, bounds: { inMs: number; outMs: number }): number {
  return Math.round(Math.max(bounds.inMs - range.inMs, Math.min(bounds.outMs - range.outMs, deltaMs)));
}
